/* @section: programs-page */
import { motion } from "framer-motion";
import PageLayout from "@/components/Layout";
import { programs } from "@/data";
import { MapPin, Users, Calendar } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as [number,number,number,number] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

function PageHero() {
  return (
    <section className="relative py-24 overflow-hidden" style={{ background: "linear-gradient(135deg, oklch(0.88 0.12 225), oklch(0.76 0.16 235))" }}>
      <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 30% 50%, oklch(0.99 0.01 50) 0%, transparent 60%)" }} />
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold uppercase tracking-widest bg-white/50 px-3 py-1 rounded-full border border-primary/30" style={{ color: "oklch(0.35 0.15 228)" }}>Our Work</span>
            <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-4 leading-tight" style={{ color: "oklch(0.15 0.08 235)" }}>Programs & Projects</h1>
          </motion.div>
          <motion.p variants={fadeUp} className="text-lg leading-relaxed" style={{ color: "oklch(0.25 0.06 230)" }}>
            From emergency humanitarian response to long-term community development — FLYN delivers comprehensive support across Southern Shan State.
          </motion.p>
          <motion.p variants={fadeUp} className="mt-3" style={{ color: "oklch(0.35 0.06 228)", fontFamily: "'Noto Sans Myanmar', sans-serif", lineHeight: 1.9 }}>
            အရေးပေါ်ကူညီမှုမှ ရေရှည်ဖွံ့ဖြိုးတိုးတက်ရေးအထိ — FLYN သည် တောင်ရှမ်းပြည်နယ်တစ်ဝှမ်းတွင် ကျယ်ကျယ်ပြန့်ပြန့် ပံ့ပိုးမှုများ ပေးလျက်ရှိ
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function ProgramsGrid() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {programs.map((prog) => (
            <motion.div key={prog.id} variants={fadeUp} className="p-8 rounded-2xl bg-card border border-border hover:border-primary/40 hover:shadow-xl transition-all duration-300 flex flex-col">
              <div className="flex items-start justify-between mb-5">
                <div className="text-4xl">{prog.icon}</div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-3 py-1 rounded-full font-semibold ${prog.status === "Active" ? "bg-primary/10 text-primary border border-primary/20" : "bg-muted text-muted-foreground"}`}>
                    {prog.status}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground font-mono">{prog.period}</span>
              </div>
              <h3 className="font-bold text-xl mb-1">{prog.title}</h3>
              <p className="text-sm font-semibold text-primary mb-3">{prog.titleFull}</p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">{prog.description}</p>
              <div className="grid grid-cols-3 gap-3 p-4 rounded-xl bg-muted/50 border border-border mt-auto">
                <div className="text-center">
                  <MapPin className="w-4 h-4 text-primary mx-auto mb-1" />
                  <p className="text-xs text-muted-foreground leading-tight">{prog.area}</p>
                </div>
                <div className="text-center border-x border-border">
                  <Users className="w-4 h-4 text-primary mx-auto mb-1" />
                  <p className="text-xs font-bold text-foreground">{prog.beneficiaries}</p>
                  <p className="text-xs text-muted-foreground">beneficiaries</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Partner</p>
                  <p className="text-xs font-semibold text-foreground leading-tight">{prog.partner}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Approach() {
  const principles = [
    { title: "Survivor & Community-Led (SCLR)", desc: "Communities lead their own recovery and development through participatory processes and collective decision-making.", icon: "🤝" },
    { title: "Do Less Harm (DLH)", desc: "Every intervention is assessed for potential negative impacts to ensure our work strengthens rather than divides communities.", icon: "☮️" },
    { title: "GEDSI Integration", desc: "Gender equality, disability inclusion, and social inclusion are core to every programme design and implementation.", icon: "🌈" },
    { title: "Conflict Sensitivity", desc: "Working in complex conflict environments, FLYN applies conflict-sensitive programming to promote peace and social cohesion.", icon: "🕊️" },
  ];
  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">How We Work</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Our Approach</h2>
          </motion.div>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {principles.map((p) => (
            <motion.div key={p.title} variants={fadeUp} className="p-6 rounded-2xl bg-background border border-border hover:border-primary/40 hover:shadow-md transition-all duration-300 text-center">
              <div className="text-4xl mb-4">{p.icon}</div>
              <h3 className="font-bold text-sm mb-2 leading-tight">{p.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default function Programs() {
  return (
    <PageLayout>
      <PageHero />
      <ProgramsGrid />
      <Approach />
    </PageLayout>
  );
}
